import React, { Fragment, useEffect, useState, memo } from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";
import { Card, CardHeader, Button, Typography } from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";
import { CellMeasurer, CellMeasurerCache } from "react-virtualized";

import { TargetingIcon } from "orion-components/SharedComponents";
import { Translate } from "orion-components/i18n";
import { timeConversion } from "client-app-core";
import { contextById } from "orion-components/ContextualData/Selectors";
import { notificationById } from "orion-components/GlobalData/Selectors";
import { acknowledgeNotifications, unAcknowledgeNotifications } from "orion-components/Dock/Notifications/actions";
import { acknowledgeAlerts, unAcknowledgeAlerts } from "orion-components/Dock/Alerts/actions";
import EventBus from "orion-components/SharedComponents/EventBus";
import { getEntityIconsByType } from "orion-components/GlobalData/Selectors";

import * as actionCreators from "./actions";
import SpriteIcon from "orion-components/SharedComponents/SpriteIcon/SpriteIcon";
import { getEcoSettingByKey } from "orion-components/Services/EcoSettingService/selectors";

const styles = {
	tabs: {
		justifyContent: "space-between",
		color: "#B5B9BE",
		padding: "0px 12px"
	},
	tab: {
		minWidth: 0,
		"&:hover": {
			color: "#FFF"
		}
	},
	selected: {
		color: "#FFF",
		opacity: 1
	},
	button: {
		textTransform: "none"
	}
};

const iconContainerStyle = {
	//verticalAlign: "super",
	paddingTop: 4,
	paddingRight: 4
	//marginRight: 4
};

const durationToMilliseconds = (duration, units) => {
	let multiplier = 1000;
	switch (units) {
		case "sec":
			multiplier = 1000;
			break;
		case "min":
			multiplier = multiplier * 60;
			break;
		case "hr":
			multiplier = multiplier * 60 * 60;
			break;
		case "day":
			multiplier = multiplier * 60 * 60 * 24;
			break;
	}
	return duration * multiplier;
};

const HtmlTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)(
	({ theme }) => ({
		[`& .${tooltipClasses.tooltip}`]: {
			backgroundColor: "#f5f5f9",
			color: "rgba(0, 0, 0, 0.87)",
			maxWidth: "none",
			fontSize: theme.typography.pxToRem(12),
			border: "1px solid #dadde9"
		}
	})
);

const propTypes = {
	closeAlertProfile: PropTypes.func,
	context: PropTypes.object,
	expanded: PropTypes.bool,
	getActivityDetails: PropTypes.func,
	handleExpand: PropTypes.func,
	loadProfile: PropTypes.func,
	measure: PropTypes.func,
	readOnly: PropTypes.bool,
	endDate: PropTypes.date,
	notification: PropTypes.object,
	openAlertProfile: PropTypes.func,
	unsubscribeFromFeed: PropTypes.func,
	timeFormat: PropTypes.string,
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	activityId: PropTypes.string,
	removeDockedCameraAndState: PropTypes.func,
	forReplay: PropTypes.bool,
	viewAlertPanelDetail: PropTypes.func
};

const defaultProps = {
	addCameraToDockMode: () => {},
	closeAlertProfile: () => {},
	context: null,
	expanded: false,
	getActivityDetails: () => {},
	handleExpand: () => {},
	loadProfile: () => {},
	measure: () => {},
	openAlertProfile: () => {},
	unsubscribeFromFeed: () => {},
	userFeeds: [],
	timeFormat: "12-hour",
	locale: "en",
	viewAlertPanelDetail: () => {}
};

const cache = new CellMeasurerCache({
	fixedWidth: true,
	defaultHeight: 140
});

const AlertProfile = props => {
	const dispatch = useDispatch();

	const { measure, id, forReplay, viewAlertPanelDetail, readOnly, selectedNotificationId, style, registerChild } =
		props;

	const { openAlertProfile } = actionCreators;

	const alertContext = useSelector(state => contextById(selectedNotificationId)(state));
	const notificationByID = useSelector(state => notificationById(id)(state) || {});
	const timeFormat = useSelector(state => state.appState.global?.timeFormat);
	const locale = useSelector(state => state.i18n.locale);
	const notification = props.notification ? props.notification : notificationByID;
	const eventLookups = useSelector(state => state.eventLookups);
	const { priorities, types } = eventLookups ? eventLookups : {};
	const colors = useSelector(state => getEntityIconsByType(state)("frame_colors"));
	const [actionTimers, setActionTimers] = useState([]);

	useEffect(() => {
		if (notification?.actions?.length) {
			const timers = notification.actions.filter(action => action.ackTimerEnabled || action.closeTimerEnabled);
			if (timers?.length) {
				const updateTimers = () => {
					const newTimers = timers.map(action => {
						const endTime =
							new Date(notification.activityDate || notification.createdDate).getTime() +
							durationToMilliseconds(action.actionTimerDuration, action.actionTimerUnit);

						const updateTimerText = () => {
							const now = new Date().getTime();
							const distance = endTime - now;

							const days = String(Math.floor(Math.abs(distance / (1000 * 60 * 60 * 24)))).padStart(
								2,
								"0"
							);
							const hours = String(
								Math.floor(Math.abs((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)))
							).padStart(2, "0");
							const minutes = String(
								Math.floor(Math.abs((distance % (1000 * 60 * 60)) / (1000 * 60)))
							).padStart(2, "0");
							const seconds = String(Math.floor(Math.abs((distance % (1000 * 60)) / 1000))).padStart(
								2,
								"0"
							);
							const timerText = `${distance < 0 ? "+" : "-"}${
								days > 0 ? `${days}:` : ""
							}${hours}:${minutes}:${seconds}`;
							return { timerText, distance };
						};

						const { timerText, distance } = updateTimerText();

						return { action, timerText, distance };
					});
					setActionTimers(newTimers);
				};
				updateTimers();

				const interval = setInterval(updateTimers, 1000);

				return () => clearInterval(interval);
			}
		}
	}, [notification?.actions]);

	const handleAlertSelection = () => {
		if (!alertContext || !alertContext.entity || !alertContext.camerasInRange || !alertContext.activities) {
			dispatch(openAlertProfile(notification, forReplay));
		}
	};

	const { message, activityDate, createdDate, geometry, eventTypeId, eventPriorityId } = notification;
	const isAcked = notification.occurrenceCount === notification.ackedCount;
	const priority = priorities?.find(item => item.id === eventPriorityId);
	const type = types?.find(item => item.id === eventTypeId);

	const handleAcknowledge = (ackOnly = false) => {
		const alertPayload = [{ alertId: notification.id, occurrenceId: "*" }];
		if (isAcked && !ackOnly) {
			if (notification?.eventPriorityId) {
				dispatch(unAcknowledgeAlerts(alertPayload));
			} else {
				dispatch(unAcknowledgeNotifications(alertPayload));
			}
		} else if (!isAcked) {
			if (notification?.eventPriorityId) {
				dispatch(acknowledgeAlerts(alertPayload));
				EventBus.publish("getAcknowledgedAlerts", {
					alertsData: notification,
					ackAll: false
				});
			} else {
				dispatch(acknowledgeNotifications(alertPayload));
			}
		}
	};

	const handleCardSelection = notification => {
		if (selectedNotificationId !== notification?.id) {
			handleAlertSelection();
			viewAlertPanelDetail({ ...notification });
		}
	};
	return (
		<CellMeasurer cache={cache} columnIndex={0} rowIndex={props.index || 0} parent={props.parent || null}>
			{({ registerChild }) => (
				<div
					ref={el => {
						registerChild && registerChild(el);
						if (props.registerChild) props.registerChild(el);
					}}
					style={props.style}
				>
					<Card
						style={{ marginBottom: 12, borderRadius: 5, cursor: "pointer", boxShadow: "none" }}
						onClick={() => handleCardSelection(notification)}
					>
						<CardHeader
							sx={{
								backgroundColor: "#494D53",
								color: "#fff",
								padding: "10px 10px 5px 2px",
								alignItems: "flex-start"
							}}
							avatar={
								<div
									style={{
										display: "flex",
										alignItems: "flex-start"
									}}
								>
									{geometry ? (
										<TargetingIcon
											geometry={geometry}
											defaultVisibility={true}
											iconContainerStyle={iconContainerStyle}
										/>
									) : null}
									<div
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											padding: 10,
											borderRadius: 5,
											backgroundColor: colors[type?.color]?.color
												? colors[type?.color]?.color
												: "#979798",
											height: 28,
											width: 28,
											marginLeft: 8
										}}
									>
										<SpriteIcon
											iconName={type?.icon || "icon_information"}
											customHeight="17.5px"
											customWidth="17.5px"
										/>
									</div>
								</div>
							}
							title={message}
							titleTypographyProps={{
								style: {
									lineHeight: "1.25em",
									fontSize: "13px"
								}
							}}
							subheader={
								<Fragment>
									{(priority || type) && (
										<div style={{ display: "flex" }}>
											<Typography style={{ fontSize: "11px" }} color="#82878C">
												{priority?.name || ""}
											</Typography>
											<Typography style={{ fontSize: "11px", margin: "0px 5px" }} color="#82878C">
												|
											</Typography>
											<Typography style={{ fontSize: "11px" }} color="#82878C">
												{type?.name || ""}
											</Typography>
										</div>
									)}
									<div style={{ display: "flex", gap: "2px" }}>
										<Typography style={{ fontSize: "11px" }} color="#82878C">
											{timeConversion.convertToUserTime(
												activityDate || createdDate,
												`full_${timeFormat}`,
												locale
											)}
										</Typography>
										{(notification.occurrenceCount || 1) > 1 && (
											<>
												<Typography style={{ fontSize: "11px" }} color="#82878C">
													|
												</Typography>
												<Typography style={{ fontSize: "11px" }} color="#82878C">
													<Translate
														value="global.profiles.alarmProfile.aggregateCount"
														count={notification.occurrenceCount}
													/>
												</Typography>
											</>
										)}
									</div>
									{!isAcked &&
										actionTimers
											.filter(timer => timer.distance > 0)
											.sort((a, b) => a.distance - b.distance)
											.slice(0, 1)
											.map((timer, index) => (
												<HtmlTooltip
													key={`${timer.action.alertActionInstanceId}_${index}`}
													title={
														<React.Fragment>
															{actionTimers.map(timer => (
																<div
																	style={{
																		display: "flex",
																		justifyContent: "space-between"
																	}}
																	key={index}
																>
																	<Typography
																		style={{ fontSize: "11px", marginRight: 8 }}
																		color="#82878C"
																	>
																		{timer.action.label}
																		{":"}
																	</Typography>
																	<Typography
																		style={{ fontSize: "11px" }}
																		color="#82878C"
																	>
																		{timer.action.result?.success ? (
																			<div>
																				<Translate value="global.profiles.alarmProfile.alarmTimer.success" />
																			</div>
																		) : timer.action.result ? (
																			<div>
																				<Translate value="global.profiles.alarmProfile.alarmTimer.failed" />
																			</div>
																		) : (
																			timer.timerText
																		)}
																	</Typography>
																</div>
															))}
														</React.Fragment>
													}
												>
													<Typography style={{ fontSize: "11px" }} color="#82878C">
														{timer.action.label}: {timer.timerText}
													</Typography>
												</HtmlTooltip>
											))}
								</Fragment>
							}
							subheaderTypographyProps={{
								component: "div",
								styles: {
									color: "#fff"
								}
							}}
							action={
								<>
									{!readOnly && !isAcked ? (
										<Button
											style={{
												height: 23,
												color: "#fff",
												minWidth: 36,
												padding: 0,
												fontSize: 11,
												margin: "0px 10px 0px 10px",
												verticalAlign: "super"
											}}
											variant="contained"
											color="primary"
											onClick={e => {
												e.stopPropagation();
												handleAcknowledge();
											}}
										>
											<Translate value="global.profiles.alarmProfile.ack" />
										</Button>
									) : null}
								</>
							}
						/>
					</Card>
				</div>
			)}
		</CellMeasurer>
	);
};

AlertProfile.propTypes = propTypes;
AlertProfile.defaultProps = defaultProps;

export default memo(withStyles(styles)(AlertProfile));
